import { Head, Form as InertiaForm, usePage } from "@inertiajs/react";
import { Button } from "@/components/ui/button.tsx";

type MonthlyPerformancePoint = {
  month: string;
  label: string;
  amount: number;
  payments_count: number;
  sessions_count: number;
};

type CollectSessionStatusPoint = {
  status: "open" | "closed" | "reconciled";
  label: string;
  count: number;
};

type TopCollectorPoint = {
  id: string;
  name: string;
  amount: number;
  payments_count: number;
};

type DashboardStats = {
  communities: number;
  users: {
    total: number;
    super_admin: number;
    admin: number;
    manager: number;
    agent: number;
  };
  tax_payers: number;
  devices: {
    total: number;
    assigned: number;
    blocked: number;
  };
  collect_sessions: {
    total: number;
    open: number;
    closed: number;
    reconciled: number;
  };
  payments: {
    total: number;
    total_collected_all_time: number;
    total_collected_last_30_days: number;
  };
  financials: {
    collected_amount: number;
    collectible_amount: number;
    outstanding_amount: number;
  };
  charts: {
    monthly_performance: MonthlyPerformancePoint[];
    collect_session_statuses: CollectSessionStatusPoint[];
    top_collectors: TopCollectorPoint[];
  };
};

type DashboardPageProps = {
  stats: DashboardStats;
};

type BarChartItem = {
  label: string;
  value: number;
  colorClass: string;
};

export default function DashboardIndex() {
  const { stats } = usePage<DashboardPageProps>().props;

  return (
    <>
      <Head title="Tableau de bord" />
      <div className="space-y-6 p-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Tableau de bord</h1>
            <p className="text-muted-foreground">Vue d'ensemble de votre activite.</p>
          </div>
          <InertiaForm method="delete" action="/users/sign_out">
            {({ processing }) => (
              <Button variant="outline" type="submit" disabled={processing}>
                {processing ? "Deconnexion..." : "Se deconnecter"}
              </Button>
            )}
          </InertiaForm>
        </div>

        <FinancialCollectionChart stats={stats} />

        <div className="grid gap-4 md:grid-cols-2">
          <OverviewChart stats={stats} />
          <UserRolesChart stats={stats} />
        </div>

        <div className="grid gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
          <MonthlyPerformanceChart data={stats.charts.monthly_performance} />
          <CollectSessionStatusChart data={stats.charts.collect_session_statuses} />
        </div>

        <TopCollectorsChart data={stats.charts.top_collectors} />
      </div>
    </>
  );
}

function FinancialCollectionChart({ stats }: { stats: DashboardStats }) {
  const items: BarChartItem[] = [
    { label: "Montant collecte", value: stats.financials.collected_amount, colorClass: "bg-chart-1" },
    { label: "Total a collecter", value: stats.financials.collectible_amount, colorClass: "bg-chart-2" },
    { label: "Reste a collecter", value: stats.financials.outstanding_amount, colorClass: "bg-chart-3" },
  ];
  const collectionRate =
    stats.financials.collectible_amount > 0
      ? Math.round((stats.financials.collected_amount / stats.financials.collectible_amount) * 100)
      : 0;

  return (
    <HorizontalBarChart
      title="Montants de collecte"
      description={`Taux de collecte: ${collectionRate}%`}
      data={items}
      valueFormatter={formatCurrency}
    />
  );
}

function OverviewChart({ stats }: { stats: DashboardStats }) {
  const items: BarChartItem[] = [
    { label: "Communautes", value: stats.communities, colorClass: "bg-chart-1" },
    { label: "Contribuables", value: stats.tax_payers, colorClass: "bg-chart-2" },
    { label: "Utilisateurs", value: stats.users.total, colorClass: "bg-chart-3" },
    { label: "Appareils", value: stats.devices.total, colorClass: "bg-chart-4" },
  ];

  return (
    <HorizontalBarChart
      title="Vue generale"
      description="Volumes principaux suivis par le tableau de bord."
      data={items}
      valueFormatter={formatNumber}
    />
  );
}

function UserRolesChart({ stats }: { stats: DashboardStats }) {
  const items: BarChartItem[] = [
    { label: "Super admin", value: stats.users.super_admin, colorClass: "bg-chart-1" },
    { label: "Admin", value: stats.users.admin, colorClass: "bg-chart-2" },
    { label: "Manager", value: stats.users.manager, colorClass: "bg-chart-3" },
    { label: "Agent", value: stats.users.agent, colorClass: "bg-chart-4" },
  ];

  return (
    <HorizontalBarChart
      title="Utilisateurs par role"
      description="Repartition des comptes actifs dans le perimetre accessible."
      data={items}
      valueFormatter={formatNumber}
    />
  );
}

function HorizontalBarChart({
  title,
  description,
  data,
  valueFormatter,
}: {
  title: string;
  description: string;
  data: BarChartItem[];
  valueFormatter: (value: number) => string;
}) {
  const maxValue = Math.max(...data.map((item) => item.value), 0);

  return (
    <section className="rounded-lg border border-border bg-card p-6">
      <h2 className="text-lg font-semibold text-foreground">{title}</h2>
      <p className="text-sm text-muted-foreground">{description}</p>

      <div className="mt-6 space-y-4">
        {data.map((item) => {
          const width = maxValue > 0 ? Math.max((item.value / maxValue) * 100, 4) : 0;

          return (
            <div key={item.label} className="grid gap-2 sm:grid-cols-[140px_minmax(0,1fr)_80px] sm:items-center">
              <p className="truncate text-sm font-medium text-foreground">{item.label}</p>
              <div className="h-3 overflow-hidden rounded-full bg-secondary">
                <div className={`h-full rounded-full ${item.colorClass}`} style={{ width: `${width}%` }} />
              </div>
              <p className="text-sm font-semibold text-foreground sm:text-right">{valueFormatter(item.value)}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function MonthlyPerformanceChart({ data }: { data: MonthlyPerformancePoint[] }) {
  const maxAmount = Math.max(...data.map((item) => item.amount), 0);
  const maxPayments = Math.max(...data.map((item) => item.payments_count), 0);

  return (
    <section className="rounded-lg border border-border bg-card p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Performance mensuelle</h2>
          <p className="text-sm text-muted-foreground">Montants encaisses et volume de paiements.</p>
        </div>
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-sm bg-chart-1" />
            Montant
          </span>
          <span className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-sm bg-chart-3" />
            Paiements
          </span>
        </div>
      </div>

      <div className="mt-6 grid h-72 grid-cols-6 items-end gap-3 border-b border-border pb-4">
        {data.map((item) => {
          const amountHeight = maxAmount > 0 ? Math.max((item.amount / maxAmount) * 100, 4) : 0;
          const paymentHeight = maxPayments > 0 ? Math.max((item.payments_count / maxPayments) * 100, 4) : 0;

          return (
            <div key={item.month} className="flex h-full min-w-0 flex-col justify-end gap-2">
              <div className="flex flex-1 items-end justify-center gap-1.5">
                <div
                  className="w-full max-w-8 rounded-t-sm bg-chart-1"
                  style={{ height: `${amountHeight}%` }}
                  title={`${formatCurrency(item.amount)} encaisses`}
                />
                <div
                  className="w-full max-w-8 rounded-t-sm bg-chart-3"
                  style={{ height: `${paymentHeight}%` }}
                  title={`${item.payments_count} paiements`}
                />
              </div>
              <div className="text-center">
                <p className="truncate text-xs font-medium text-foreground">{item.label}</p>
                <p className="truncate text-[11px] text-muted-foreground">{formatCurrency(item.amount)}</p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function CollectSessionStatusChart({ data }: { data: CollectSessionStatusPoint[] }) {
  const total = data.reduce((sum, item) => sum + item.count, 0);

  return (
    <section className="rounded-lg border border-border bg-card p-6">
      <h2 className="text-lg font-semibold text-foreground">Etat des collectes</h2>
      <p className="text-sm text-muted-foreground">Repartition des sessions de collecte.</p>

      <div className="mt-6 space-y-4">
        {data.map((item, index) => {
          const percent = total > 0 ? Math.round((item.count / total) * 100) : 0;
          const colorClass = ["bg-chart-2", "bg-chart-4", "bg-chart-5"][index] ?? "bg-chart-2";

          return (
            <div key={item.status}>
              <div className="mb-2 flex items-center justify-between gap-3 text-sm">
                <span className="font-medium text-foreground">{item.label}</span>
                <span className="text-muted-foreground">
                  {item.count} ({percent}%)
                </span>
              </div>
              <div className="h-3 overflow-hidden rounded-full bg-secondary">
                <div className={`h-full rounded-full ${colorClass}`} style={{ width: `${percent}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function TopCollectorsChart({ data }: { data: TopCollectorPoint[] }) {
  const maxAmount = Math.max(...data.map((item) => item.amount), 0);

  return (
    <section className="rounded-lg border border-border bg-card p-6">
      <h2 className="text-lg font-semibold text-foreground">Meilleurs collecteurs</h2>
      <p className="text-sm text-muted-foreground">Classement par montant encaisse.</p>

      <div className="mt-6 space-y-4">
        {data.length === 0 ? (
          <p className="rounded-md border border-border bg-secondary/60 px-4 py-3 text-sm text-muted-foreground">
            Aucune collecte enregistree pour le moment.
          </p>
        ) : (
          data.map((collector) => {
            const width = maxAmount > 0 ? Math.max((collector.amount / maxAmount) * 100, 4) : 0;

            return (
              <div key={collector.id} className="grid gap-2 sm:grid-cols-[180px_minmax(0,1fr)_140px] sm:items-center">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-foreground">{collector.name}</p>
                  <p className="text-xs text-muted-foreground">{collector.payments_count} paiements</p>
                </div>
                <div className="h-3 overflow-hidden rounded-full bg-secondary">
                  <div className="h-full rounded-full bg-chart-1" style={{ width: `${width}%` }} />
                </div>
                <p className="text-sm font-semibold text-foreground sm:text-right">{formatCurrency(collector.amount)}</p>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}

function formatCurrency(value: number) {
  return value.toLocaleString("fr-FR", {
    maximumFractionDigits: 0,
  });
}

function formatNumber(value: number) {
  return value.toLocaleString("fr-FR");
}
